/**
 * Procurement / Inventory API
 * Uses the app's shared axios client (api-client.ts) so auth headers and
 * 401 handling are consistent with every other feature in the app.
 */
import { api } from "#/lib/api-client";
import type {
  InventoryCategory,
  InventoryCategoryCreateRequest,
  InventoryCategoryUpdateRequest,
  InventoryImportBatchSummary,
  InventoryImportRow,
  InventoryItem,
  InventoryMovement,
  InventoryItemAdjustmentRequest,
  InventoryItemCreateRequest,
  InventoryItemUpdateRequest,
  InventoryRequestApprovalRequest,
  InventoryRequestCancelRequest,
  InventoryRequestDetails,
  InventoryRequestSummary,
  PageResponse,
} from "./types";

const BASE = "/api/hr/inventory";

// ─── helpers ────────────────────────────────────────────────────────────────

function qs(params: Record<string, string | number | undefined | null>) {
  const p: Record<string, string> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== "") p[k] = String(v);
  });
  return p;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizePage<T>(raw: any): PageResponse<T> {
  const body = raw?.content !== undefined ? raw : (raw?.data ?? raw);
  const meta = body?.page ?? body;
  return {
    content:       body?.content       ?? [],
    totalElements: meta?.totalElements ?? meta?.total_elements ?? meta?.total ?? 0,
    totalPages:    meta?.totalPages    ?? meta?.total_pages    ?? 0,
    number:        meta?.number        ?? 0,
    size:          meta?.size          ?? 0,
    first:         meta?.number === 0,
    last:          (meta?.number ?? 0) + 1 >= (meta?.totalPages ?? meta?.total_pages ?? 1),
  };
}

// ─── categories ─────────────────────────────────────────────────────────────

export const inventoryCategoryApi = {
  /** `active` filters server-side; omit to get everything (used by the category admin page). */
  list(params: { active?: boolean } = {}): Promise<InventoryCategory[]> {
    return api
      .get<InventoryCategory[]>(`${BASE}/categories`, {
        params: qs({ active: params.active === undefined ? undefined : String(params.active) }),
      })
      .then((r) => r.data);
  },
  create(payload: InventoryCategoryCreateRequest): Promise<InventoryCategory> {
    return api.post<InventoryCategory>(`${BASE}/categories`, payload).then((r) => r.data);
  },
  update(id: number, payload: InventoryCategoryUpdateRequest): Promise<InventoryCategory> {
    return api.put<InventoryCategory>(`${BASE}/categories/${id}`, payload).then((r) => r.data);
  },
  activate(id: number): Promise<InventoryCategory> {
    return api.put<InventoryCategory>(`${BASE}/categories/${id}/activate`).then((r) => r.data);
  },
  deactivate(id: number): Promise<InventoryCategory> {
    return api.put<InventoryCategory>(`${BASE}/categories/${id}/deactivate`).then((r) => r.data);
  },
};

// ─── items ───────────────────────────────────────────────────────────────────

export const inventoryApi = {
  // Items
  listItems(params: {
    q?: string;
    page?: number;
    size?: number;
    itemType?: string;
    categoryId?: number | "";
    status?: string;
    stockFilter?: "LOW" | "GOOD" | "OUT" | "";
  }): Promise<PageResponse<InventoryItem>> {
    return api
      .get(`${BASE}/items`, {
        params: qs({
          q: params.q,
          page: params.page ?? 0,
          size: params.size ?? 10,
          itemType: params.itemType,
          categoryId: params.categoryId,
          status: params.status,
          stockFilter: params.stockFilter,
        }),
      })
      .then((r) => normalizePage<InventoryItem>(r.data));
  },

  getItem(itemId: number): Promise<InventoryItem> {
    return api.get<InventoryItem>(`${BASE}/items/${itemId}`).then((r) => r.data);
  },

  createItem(payload: InventoryItemCreateRequest): Promise<InventoryItem> {
    return api.post<InventoryItem>(`${BASE}/items`, payload).then((r) => r.data);
  },

  updateItem(itemId: number, payload: InventoryItemUpdateRequest): Promise<InventoryItem> {
    return api.put<InventoryItem>(`${BASE}/items/${itemId}`, payload).then((r) => r.data);
  },

  adjustItem(itemId: number, payload: InventoryItemAdjustmentRequest): Promise<InventoryItem> {
    return api.post<InventoryItem>(`${BASE}/items/${itemId}/adjust`, payload).then((r) => r.data);
  },

  /**
   * New — restock/stock-movement history. One of itemId/categoryId is required by the backend
   * (400 otherwise); month requires year to also be set.
   */
  listMovements(params: {
    itemId?: number;
    categoryId?: number;
    year?: number;
    month?: number;
    page?: number;
    size?: number;
  }): Promise<PageResponse<InventoryMovement>> {
    return api
      .get(`${BASE}/movements`, {
        params: qs({
          itemId: params.itemId,
          categoryId: params.categoryId,
          year: params.year,
          month: params.month,
          page: params.page ?? 0,
          size: params.size ?? 20,
        }),
      })
      .then((r) => normalizePage<InventoryMovement>(r.data));
  },

  // Requests
  listMine(page = 0, size = 10): Promise<PageResponse<InventoryRequestSummary>> {
    return api.get(`${BASE}/requests/mine`, { params: { page, size } }).then((r) => normalizePage<InventoryRequestSummary>(r.data));
  },

  listInbox(page = 0, size = 10): Promise<PageResponse<InventoryRequestSummary>> {
    return api.get(`${BASE}/requests/inbox`, { params: { page, size } }).then((r) => normalizePage<InventoryRequestSummary>(r.data));
  },

  getRequest(id: number): Promise<InventoryRequestDetails> {
    return api.get<InventoryRequestDetails>(`${BASE}/requests/${id}`).then((r) => r.data);
  },

  // NOTE: creating new Inventory Requests is retired backend-side — POST /requests now
  // always returns 410 Gone. Use requisitions/api.ts (hrRequisitionApi.create with
  // requisitionType=GOODS) instead. Do not add createRequest/addLine/updateLine/deleteLine
  // back here; the whole draft-editing surface for this resource has no UI consumer since
  // there's no way to create a fresh draft to edit.

  submitRequest(id: number): Promise<InventoryRequestDetails> {
    return api.post<InventoryRequestDetails>(`${BASE}/requests/${id}/submit`).then((r) => r.data);
  },

  approveRequest(id: number, payload: InventoryRequestApprovalRequest): Promise<InventoryRequestDetails> {
    return api.post<InventoryRequestDetails>(`${BASE}/requests/${id}/approve`, payload).then((r) => r.data);
  },

  rejectRequest(id: number, payload: InventoryRequestApprovalRequest): Promise<InventoryRequestDetails> {
    return api.post<InventoryRequestDetails>(`${BASE}/requests/${id}/reject`, payload).then((r) => r.data);
  },

  cancelRequest(id: number, payload: InventoryRequestCancelRequest): Promise<InventoryRequestDetails> {
    return api.post<InventoryRequestDetails>(`${BASE}/requests/${id}/cancel`, payload).then((r) => r.data);
  },

  issueRequest(id: number): Promise<InventoryRequestDetails> {
    return api.post<InventoryRequestDetails>(`${BASE}/requests/${id}/issue`).then((r) => r.data);
  },

  // Imports
  downloadItemMasterTemplate(): Promise<Blob> {
    return api.get(`${BASE}/imports/templates/item-master`, { responseType: "blob" }).then((r) => r.data as Blob);
  },

  downloadStockTemplate(): Promise<Blob> {
    return api.get(`${BASE}/imports/templates/stock`, { responseType: "blob" }).then((r) => r.data as Blob);
  },

  uploadItemMaster(file: File): Promise<InventoryImportBatchSummary> {
    const form = new FormData();
    form.append("file", file);
    return api.post<InventoryImportBatchSummary>(`${BASE}/imports/item-master`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },

  uploadStockCorrection(file: File): Promise<InventoryImportBatchSummary> {
    const form = new FormData();
    form.append("file", file);
    return api.post<InventoryImportBatchSummary>(`${BASE}/imports/stock-correction`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },

  submitImport(batchId: number): Promise<InventoryImportBatchSummary> {
    return api.post<InventoryImportBatchSummary>(`${BASE}/imports/${batchId}/submit`).then((r) => r.data);
  },

  approveImport(batchId: number, comments?: string | null): Promise<InventoryImportBatchSummary> {
    return api.post<InventoryImportBatchSummary>(`${BASE}/imports/${batchId}/approve`, { comments }).then((r) => r.data);
  },

  rejectImport(batchId: number, comments?: string | null): Promise<InventoryImportBatchSummary> {
    return api.post<InventoryImportBatchSummary>(`${BASE}/imports/${batchId}/reject`, { comments }).then((r) => r.data);
  },

  recallImport(batchId: number): Promise<InventoryImportBatchSummary> {
    return api.post<InventoryImportBatchSummary>(`${BASE}/imports/${batchId}/recall`).then((r) => r.data);
  },

  postImport(batchId: number): Promise<InventoryImportBatchSummary> {
    return api.post<InventoryImportBatchSummary>(`${BASE}/imports/${batchId}/post`).then((r) => r.data);
  },

  listImports(page = 0, size = 10, status?: string): Promise<PageResponse<InventoryImportBatchSummary>> {
    const params: Record<string, string | number> = { page, size };
    if (status) params.status = status;
    return api.get(`${BASE}/imports`, { params }).then((r) => normalizePage<InventoryImportBatchSummary>(r.data));
  },

  /** Approval inbox — PENDING_APPROVAL batches oldest-first (requires HR_INVENTORY_APPROVE) */
  listPendingApproval(page = 0, size = 20): Promise<PageResponse<InventoryImportBatchSummary>> {
    return api.get(`${BASE}/imports/pending-approval`, { params: { page, size } }).then((r) => normalizePage<InventoryImportBatchSummary>(r.data));
  },

  /** New — per-row detail for an import batch, optionally errors-only */
  listImportRows(
    batchId: number,
    params: { onlyErrors?: boolean; page?: number; size?: number } = {},
  ): Promise<PageResponse<InventoryImportRow>> {
    return api
      .get(`${BASE}/imports/${batchId}/rows`, {
        params: qs({
          onlyErrors: params.onlyErrors ? "true" : undefined,
          page: params.page ?? 0,
          size: params.size ?? 20,
        }),
      })
      .then((r) => normalizePage<InventoryImportRow>(r.data));
  },

  /** New — download failed rows for a batch as .xlsx with an Error Reason column */
  downloadImportErrorReport(batchId: number): Promise<Blob> {
    return api.get<Blob>(`${BASE}/imports/${batchId}/error-report`, { responseType: "blob" }).then((r) => r.data as Blob);
  },
  /**
   * V69 — Export inventory item catalogue as Excel (Blob).
   * Filters are passed through so the export matches whatever the user is currently viewing;
   * backend now filters server-side (q/itemType/categoryId/status/stockFilter) and includes
   * a "Last Updated" column matching InventoryItem.updatedAt.
   */
  exportItems(params: {
    q?: string;
    itemType?: string;
    categoryId?: number | "";
    status?: string;
    stockFilter?: "LOW" | "GOOD" | "OUT" | "";
  } = {}): Promise<Blob> {
    return api
      .get<Blob>(`${BASE}/items/export`, {
        responseType: "blob",
        params: qs({
          q: params.q,
          itemType: params.itemType,
          categoryId: params.categoryId,
          status: params.status,
          stockFilter: params.stockFilter,
        }),
      })
      .then((r) => r.data as Blob);
  },

  /** New 2026-07-08 — one workbook covering every inventory submodule (Summary + one sheet each). */
  exportMasterReport(): Promise<Blob> {
    return api.get<Blob>(`/api/hr/inventory/reports/master/export`, { responseType: "blob" }).then((r) => r.data as Blob);
  },
};

